import ShaderService from "../shader/shader_service";
import ResourceService from "../resource/resourceservice";
import RenderObject from "./renderobject";
import CollisionService from "../collision/collision_service";
import Camera from "../scene/camera";

export default class RenderService {
  private shader_service: ShaderService;
  private resource_service: ResourceService;
  private collision_service: CollisionService;
  canvas: HTMLCanvasElement | null = null;
  private context: GPUCanvasContext | null = null;

  private format: GPUTextureFormat = "rgba8unorm";
  private animationFrameId: number | null = null;
  private render_objects: RenderObject[];

  private depthTexture: GPUTexture | null = null;
  private depthView: GPUTextureView | null = null;
  private depthFormat: GPUTextureFormat = "depth24plus";

  private presentationWidth = 1;
  private presentationHeight = 1;
  private camera:Camera| null = null;

  constructor(
    shader_service: ShaderService,
    resource_service: ResourceService,
    collision_service: CollisionService
  ) {
    this.shader_service = shader_service;
    this.resource_service = resource_service;
    this.collision_service = collision_service;
    this.render_objects = [];
  }

  private get device(): GPUDevice | null {
    return this.shader_service?.get_device() ?? null;
  }

  set_camera(camera:Camera)
  {
      this.camera = camera;
  }

  private configureContext() {
    if (!this.device || !this.context) return;

    this.context.configure({
      device: this.device,
      format: this.format,
      alphaMode: "premultiplied",
    });
  }

  private destroyDepthTexture() {
    if (this.depthTexture) {
      this.depthTexture.destroy();
      this.depthTexture = null;
    }
    this.depthView = null;
  }

  private recreateDepthTexture() {
    if (!this.device || !this.canvas) return;

    this.destroyDepthTexture();

    this.depthTexture = this.device.createTexture({
      size: {
        width: this.canvas.width,
        height: this.canvas.height,
        depthOrArrayLayers: 1,
      },
      format: this.depthFormat,
      usage: GPUTextureUsage.RENDER_ATTACHMENT,
    });

    this.depthView = this.depthTexture.createView();
  }

  reset(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    this.context = this.canvas.getContext("webgpu") as GPUCanvasContext | null;
    if (!this.context || !this.device) return;

    this.configureContext();
    this.resize();
  }

  add(obj: RenderObject) {
    this.render_objects.push(obj);
  }

  build() {
    if (!this.device) return;

    this.collision_service.build(
      this.device,
      this.format,
      this.shader_service,
      this.resource_service
    );
    for (const obj of this.render_objects) {
      obj.build(this.device, this.format, this.shader_service, this.resource_service);
    }
  }

  clear() {
    this.render_objects = [];
  }

  get_format(): GPUTextureFormat {
    return this.format;
  }

  get_context(): GPUCanvasContext | null {
    return this.context;
  }

  get_depth_format(): GPUTextureFormat {
    return this.depthFormat;
  }

  resize(width?: number, height?: number, dpr?: number): boolean {
    if (!this.canvas) return false;

    const rect = this.canvas.getBoundingClientRect();
    const pixelRatio = Math.min(dpr ?? window.devicePixelRatio ?? 1, 2);

    const nextWidth = Math.max(1, Math.round((width ?? rect.width) * pixelRatio));
    const nextHeight = Math.max(1, Math.round((height ?? rect.height) * pixelRatio));

    const resized =
      this.canvas.width !== nextWidth || this.canvas.height !== nextHeight;

    if (!resized) return false;

    this.canvas.width = nextWidth;
    this.canvas.height = nextHeight;
    this.presentationWidth = nextWidth;
    this.presentationHeight = nextHeight;

    this.configureContext();
    this.recreateDepthTexture();

    return true;
  }

  start() {
    if (this.animationFrameId === null) {
      this.animationFrameId = requestAnimationFrame(this.frame);
    }
  }

  private frame = (time: number) => {
    this.render(time);
    this.animationFrameId = requestAnimationFrame(this.frame);
  };

  stop() {
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
  }

  render(time: number) {
    if (!this.device || !this.context || !this.canvas) return;

    this.resize();

    if (!this.depthView) {
      this.recreateDepthTexture();
      if (!this.depthView) return;
    }

    const encoder = this.device.createCommandEncoder();

    // --- Compute phase --------------------------------------------------
    const compute_pass = encoder.beginComputePass({
      label: "render_service_compute_pass",
    });
    for (const obj of this.render_objects) {
      obj.compute(this.device, compute_pass,encoder);
    }
    compute_pass.end();

    // --- Render phase ---------------------------------------------------
    for (const obj of this.render_objects)
    {
      if(this.camera)
      {
        obj.prepare(this.device,this.camera);
      }
    }

    const view = this.context.getCurrentTexture().createView();

    const render_pass = encoder.beginRenderPass({
      colorAttachments: [
        {
          view,
          clearValue: { r: 0, g: 0, b: 0, a: 1 },
          loadOp: "clear",
          storeOp: "store",
        },
      ],
      depthStencilAttachment: {
        view: this.depthView,
        depthClearValue: 1.0,
        depthLoadOp: "clear",
        depthStoreOp: "store",
      },
    });

    for (const obj of this.render_objects) 
    {
      if(obj.layer == 0)
      {
         obj.draw(render_pass);
      }
    }

    for (const obj of this.render_objects) 
    {
      if(obj.layer == 1)
      {
      obj.draw(render_pass);
      }
    }



    this.collision_service.draw(render_pass);

    render_pass.end();

    // --- Readback encode phase (before submit) --------------------------
    for (const obj of this.render_objects) {
      obj.readback_encode(this.device, encoder);
    }

    // --- Submit ---------------------------------------------------------
    this.device.queue.submit([encoder.finish()]);

    // --- Post-submit cleanup --------------------------------------------
    for (const obj of this.render_objects) {
      obj.finish_draw();
    }

    // --- Readback poll (drives mapAsync) --------------------------------
    for (const obj of this.render_objects) 
    {
          obj.readback_poll(this.device);
    }
  }
}